import {api} from "@/shared/api";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {formatPercent} from "@/shared/utils/misc";
import {DOMUtil} from "@/shared/utils/dom";
import {formatDate, isSameOrAfterDay, Temporal} from "@/shared/utils/datefunctions";
import {FeatureModule, PayableMonths} from "@/shared/timr";

export interface ExtendRuntimeDialogConfig {
	selectedFeatureModules: FeatureModule[];
	userCount: number;
	validTo: Temporal.PlainDate | null;
	currentDate: Temporal.PlainDate | null;
	extendByMonths?: number;
	free: boolean;
}

const template = `
				<div id="extendRuntimeDialog">
					<div class="flex-row">
						<div class="flex-column-30">
							${messages["pricing.extendRuntimeDialog.currentValidTo"]}
						</div>
						<div class="flex-column-10"></div>
						<div data-current-valid-to class="flex-column-60"></div>
					</div>
					<div class="flex-row mt-20">
						<div class="flex-column-30 pt-8">
							${messages["pricing.extendRuntimeDialog.extendBy"]}
						</div>
						<div class="flex-column-10"></div>
						<div class="flex-column-60">
							<select data-months-select class="form-control"></select>
						</div>
					</div>
					<div class="flex-row mt-20">
						<div class="flex-column-30">
							${messages["pricing.extendRuntimeDialog.newValidTo"]}
						</div>
						<div class="flex-column-10"></div>
						<div data-new-valid-to class="flex-column-60"></div>
					</div>
				</div>
			`;

/**
 * Custom web component that draws the content of the runtime extension dialog.
 */
export class ExtendRuntimeDialog extends HTMLElement {

	private static readonly DEFAULT_SELECTED_MONTHS: string = "12";

	private config: ExtendRuntimeDialogConfig;
	private callback: (extendByMonths: number) => void;

	private monthsSelectElement: JQuery;

	public static show(config: ExtendRuntimeDialogConfig, callback: (extendByMonths: number) => void) {
		const dialog = document.createElement("extend-runtime-dialog") as ExtendRuntimeDialog;
		dialog.config = config;
		dialog.callback = callback;
		dialog.show();
	}

	private connectedCallback() {
		this.innerHTML = template;
		this.monthsSelectElement = $("[data-months-select]");
		this.initListeners();
		this.loadPayableMonths();
		this.initDialogValues();
	}

	private show() {
		new BootstrapModal.Modal({
			title: "pricing.extendRuntimeDialog.title",
			message: $(this),
			destroyOnClose: true,
			buttons: [
				{
					text: "dialog.cancel",
					css: "cancelButton btn-default"
				},
				{
					text: "dialog.applySelection",
					css: "saveButton btn-primary",
					action: (m) => {
						this.callback(Number(this.monthsSelectElement.val()));
						m.close();
					}
				}
			],
		}).show();
	}

	private initListeners() {
		this.monthsSelectElement.change(() => this.onSelectionChange());
	}

	private loadPayableMonths() {
		void api("pricing/findMonthsToExtendRuntime.json")
			.get()
			.done((data: PayableMonths[]) => {
				this.fillPayableMonthsSelect(data);
			});
	}

	private initDialogValues() {
		$("[data-current-valid-to]").text(this.config.free ? "" : formatDate(this.config.validTo));
	}

	private fillPayableMonthsSelect(payableMonths: PayableMonths[]) {
		DOMUtil.fillSelect(
			this.monthsSelectElement,
			payableMonths,
			false,
			(item: PayableMonths) => {
				const runtimeDiscountFormatted = `(${formatPercent(item.discountPercentage, 0)} ${messages["pricing.extendRuntimeDialog.rebate"]})`;
				return DOMUtil.createOption(String(item.months),
					`${item.months} ${item.discountPercentage ? runtimeDiscountFormatted : ""}`
				);
			},
			false,
			true,
			this.config.extendByMonths ? String(this.config.extendByMonths) : ExtendRuntimeDialog.DEFAULT_SELECTED_MONTHS
		);
		this.onSelectionChange();
	}

	private onSelectionChange() {
		const months = Number(this.monthsSelectElement.val());
		const newRuntimeValidFrom = isSameOrAfterDay(this.config.validTo, this.config.currentDate) ? this.config.validTo : this.config.currentDate;
		const newValidToDate = newRuntimeValidFrom?.add({months: months});
		$("[data-new-valid-to]").text(formatDate(newValidToDate));
	}
}
